/**
 * Native auth deep-link helper.
 *
 * On native (Capacitor) builds we cannot rely on a normal web redirect to
 * carry the session_id back into the app, because the browser instance that
 * completes the Google flow is the system browser — not the app's WebView.
 *
 * Flow:
 *   1. App calls `startNativeGoogleSignIn()`.
 *   2. We open the Emergent auth URL in the system browser, asking it to
 *      redirect back to `zenjong://auth-callback` once the user is signed in.
 *   3. Android/iOS intercepts that custom-scheme URL and re-opens the app
 *      with an `appUrlOpen` event whose URL contains `?session_id=...`.
 *   4. We exchange that session_id with the backend via /auth/session and
 *      then call the supplied `onSession(token)` callback so the React tree
 *      can refresh auth context + navigate to /dashboard.
 *
 * Web builds keep the existing redirect-based flow.
 *
 * AndroidManifest.xml requires an intent-filter with
 *   <data android:scheme="zenjong" android:host="auth-callback" />
 * inside the MainActivity. iOS Info.plist needs a CFBundleURLSchemes entry
 * for "zenjong". Both are documented in /app/ADMOB_SETUP.md.
 */
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { isNative } from "./admob";

export const NATIVE_AUTH_SCHEME = "zenjong://auth-callback";

let _listenerHandle = null;

export async function startNativeGoogleSignIn(onSession) {
  if (!isNative()) {
    throw new Error("Native sign-in helper called on web build");
  }

  // Always tear down any previous listener before adding a new one.
  if (_listenerHandle) {
    try { await _listenerHandle.remove(); } catch (_) {}
    _listenerHandle = null;
  }

  // Listen for the deep-link callback that brings the user back into the app.
  _listenerHandle = await CapApp.addListener("appUrlOpen", async (event) => {
    try {
      const url = event?.url || "";
      if (!url.startsWith("zenjong://auth-callback")) return;

      // Extract session_id from query OR fragment ("?session_id=" or "#session_id=").
      let sid = null;
      const qIdx = url.indexOf("?");
      const hIdx = url.indexOf("#");
      const tail = (qIdx >= 0 ? url.slice(qIdx + 1) : "")
                 + (hIdx >= 0 ? "&" + url.slice(hIdx + 1) : "");
      const params = new URLSearchParams(tail);
      sid = params.get("session_id");

      if (sid) {
        await Browser.close().catch(() => {});
        // Brief delay — when Android resumes the app from the system browser,
        // the WebView's network stack sometimes isn't fully reconnected for a
        // few hundred ms. A bare /api/auth/session call here often returns
        // Network Error on the first try, but succeeds on retry. Sleep then
        // hand off to the callback, which already retries via React refresh.
        await new Promise((r) => setTimeout(r, 400));
        try {
          await onSession(sid);
        } catch (firstErr) {
          // One retry after another 800ms — handles the "first attempt fails,
          // second succeeds" pattern testers reported.
          console.warn("[native-auth] first session exchange failed, retrying:", firstErr?.message || firstErr);
          await new Promise((r) => setTimeout(r, 800));
          await onSession(sid);
        }
      }
    } catch (e) {
      console.error("[native-auth] callback failed:", e);
    } finally {
      if (_listenerHandle) {
        try { await _listenerHandle.remove(); } catch (_) {}
        _listenerHandle = null;
      }
    }
  });

  // Kick off the OAuth flow in the system browser. Emergent auth will redirect
  // to ${redirect} with #session_id=... appended.
  const redirect = NATIVE_AUTH_SCHEME;
  const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  await Browser.open({ url: authUrl });
}
